"use client";

import { useRef, useEffect } from 'react';
import { Star, Quote, BadgeCheck } from 'lucide-react';

const reviews = [
  {
    name: "Bhupinder Singh",
    date: "8 June 2025",
    text: "Great app! Highly recommend! The service was professional and the booking process was seamless.",
    rating: 5,
    service: "Deep Cleaning",
    initials: "BS"
  },
  {
    name: "Satveer Lubana",
    date: "8 June 2025",
    text: "Good at service. Very punctual and thorough with their work. Will definitely book again!",
    rating: 5,
    service: "Kitchen Cleaning",
    initials: "SL"
  },
  {
    name: "Naman Jain",
    date: "10 July 2025",
    text: "I just love the services! The team was friendly and did an amazing job cleaning my home.",
    rating: 5,
    service: "Full House Cleaning",
    initials: "NJ"
  },
  {
    name: "Dalmeet Kumar",
    date: "8 June 2025",
    text: "Excellent work👍 Very satisfied with the quality and professionalism of the service.",
    rating: 5,
    service: "Bathroom Cleaning",
    initials: "DK"
  },
  {
    name: "Sumit Lubana",
    date: "8 June 2025",
    text: "Highly professional team. They arrived on time and completed the work efficiently.",
    rating: 5,
    service: "Sofa Cleaning",
    initials: "SL"
  },
  {
    name: "Baljeet Kaur",
    date: "8 June 2025",
    text: "🥰 Amazing experience! The service exceeded my expectations. Very happy with the results.",
    rating: 5,
    service: "Deep Cleaning",
    initials: "BK"
  },
  {
    name: "Toshal Lubana",
    date: "17 June 2025",
    text: "Great job. The cleaning was thorough and the staff was very courteous and professional.",
    rating: 5,
    service: "Kitchen Cleaning",
    initials: "TL"
  },
  {
    name: "Vinayak Prashar",
    date: "1 July 2025",
    text: "Great Services! Easy booking and excellent quality. Highly recommend SortKaro to everyone.",
    rating: 5,
    service: "Full House Cleaning",
    initials: "VP"
  },
  {
    name: "Sahil Rana",
    date: "8 June 2025",
    text: "Very good service. The team was professional and did a fantastic job. Will use again!",
    rating: 5,
    service: "Bathroom Cleaning",
    initials: "SR"
  },
];

export default function InfiniteMotionTestimonials() {
  const scrollRef = useRef(null);

  // Triple the reviews for seamless infinite scroll
  const infiniteReviews = [...reviews, ...reviews, ...reviews];

  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer) return;

    let animationId;
    let scrollPosition = 0;
    const cardWidth = 380; // card width + gap
    const totalWidth = reviews.length * cardWidth;

    const scroll = () => {
      scrollPosition += 0.6; // Smooth scroll speed

      if (scrollPosition >= totalWidth) {
        scrollPosition = 0;
      }

      scrollContainer.scrollLeft = scrollPosition;
      animationId = requestAnimationFrame(scroll);
    };

    animationId = requestAnimationFrame(scroll);

    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [reviews.length]);

  return (
    <section className="py-16 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Star className="w-8 h-8 fill-yellow-400 text-yellow-400" />
            <h2 className="text-4xl font-extrabold text-gray-900">
              What Our Customers Say
            </h2>
          </div>
          <div className="flex items-center justify-center gap-2">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <span className="text-gray-600 font-semibold">4.8 out of 5</span>
            <span className="text-gray-400">•</span>
            <span className="text-gray-600">350+ reviews</span>
          </div>
        </div>

        {/* Horizontal Scrolling Testimonials */}
        <div className="relative">
          {/* Gradient Overlays */}
          <div className="absolute left-0 top-0 bottom-0 w-24 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-24 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

          {/* Scrollable Container */}
          <div
            ref={scrollRef}
            className="flex gap-6 overflow-x-hidden scrollbar-hide py-4"
            style={{ scrollBehavior: 'auto' }}
          >
            {infiniteReviews.map((review, index) => (
              <div
                key={index}
                className={`
                  min-w-[360px] max-w-[360px] flex-shrink-0
                  bg-gradient-to-br from-white to-gray-50
                  rounded-2xl p-6 
                  border border-gray-100
                  shadow-[0_2px_8px_rgba(0,0,0,0.04)]
                  hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)]
                  hover:-translate-y-1
                  transition-all duration-300 ease-out
                  cursor-pointer
                  relative
                  overflow-hidden
                `}
              >
                {/* Quote Icon Background */}
                <Quote className="absolute -top-2 -right-2 w-20 h-20 text-gray-100 opacity-50" />

                {/* User Info */}
                <div className="flex items-start gap-3 mb-4 relative z-10">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00489b] to-[#0066cc] flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md">
                    {review.initials}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-bold text-gray-900">{review.name}</span>
                      <BadgeCheck className="w-4 h-4 text-blue-500" />
                    </div>
                    <span className="text-xs text-gray-500">{review.date}</span>
                  </div>
                </div>

                {/* Star Rating */}
                <div className="flex gap-1 mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>

                {/* Review Text */}
                <p className="text-gray-700 text-sm leading-relaxed mb-4 italic relative z-10">
                  "{review.text}"
                </p>

                {/* Service Badge */}
                <div className="flex items-center gap-2 pt-3 border-t border-gray-100">
                  <div className="bg-blue-50 text-[#00489b] text-xs font-semibold px-3 py-1.5 rounded-full">
                    {review.service}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
